import {
  type CompensatoryCreateInput,
  type CompensatoryRequestPage,
  compensatoryRequestPageSchema,
} from "@/features/leave/schemas/compensatory.schema";
import {
  type MyApplications,
  type MyBalances,
  type MyLeavePeriods,
  myApplicationsSchema,
  myBalancesSchema,
  myLeavePeriodsSchema,
} from "@/features/leave/schemas/leave.schema";
import { type LedgerView, ledgerViewSchema } from "@/features/leave/schemas/leave-ledger.schema";
import {
  type LeaveRegister,
  leaveRegisterSchema,
} from "@/features/leave/schemas/leave-register.schema";
import type { BalanceReport } from "@/features/leave/schemas/leave-report.schema";
import {
  balanceReportSchema,
  type LeaveBalanceSummary,
  type LeaveCalendar,
  leaveBalanceSummarySchema,
  leaveCalendarSchema,
  type OutToday,
  outTodaySchema,
} from "@/features/leave/schemas/leave-report.schema";
import { apiCall } from "@/shared/lib/api-call";
import { apiPaths } from "@/shared/lib/api-paths";
import { downloadFile } from "@/shared/lib/download-file";

/// HTTP boundary for the leave module's six read surfaces (M17,
/// task-17-report.md). Every response is Zod-parsed through the shared
/// `apiCall` helper (T3 / FE-SEC6). Every day figure crosses the wire as a
/// STRING; never parse it to a number here or anywhere downstream (Decimal(9,4)
/// on the shore, string discipline documented on each schema in
/// `schemas/leave.schema.ts`). A local `qs` helper: this file cannot import
/// attendance's (G2, and it isn't exported there either).

const P = apiPaths.leave;

export async function listMyCompensatoryRequests(
  workspaceId: string,
  filters: { status?: string; limit?: number; cursor?: string } = {},
): Promise<CompensatoryRequestPage> {
  return apiCall<CompensatoryRequestPage>(
    `${P.compensatoryRequests(workspaceId)}${qs({
      status: filters.status,
      limit: filters.limit?.toString(),
      cursor: filters.cursor,
    })}`,
    { method: "GET" },
    compensatoryRequestPageSchema,
  );
}

export async function createCompensatoryRequest(
  workspaceId: string,
  input: CompensatoryCreateInput,
): Promise<CompensatoryRequestPage["rows"][number]> {
  return apiCall<CompensatoryRequestPage["rows"][number]>(
    P.compensatoryRequests(workspaceId),
    {
      method: "POST",
      body: JSON.stringify({
        workFromDate: input.workedDate,
        workEndDate: input.workedDate,
        halfDay: input.halfDay,
        reason: input.reason.trim(),
      }),
    },
    compensatoryRequestPageSchema.shape.rows.element,
  );
}

function qs(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, value);
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}

export function downloadLeaveRegister(
  workspaceId: string,
  filters: {
    from: string;
    to: string;
    leaveTypeId?: string;
    membershipId?: string;
    departmentId?: string;
  },
): Promise<void> {
  return downloadFile(
    `${P.registerExportCsv(workspaceId)}${qs(filters)}`,
    `leave-register-${filters.from}-${filters.to}.csv`,
  );
}

export async function listLeaveRegister(
  workspaceId: string,
  filters: {
    from: string;
    to: string;
    leaveTypeId?: string;
    membershipId?: string;
    departmentId?: string;
  },
): Promise<LeaveRegister> {
  return apiCall<LeaveRegister>(
    `${P.register(workspaceId)}${qs(filters)}`,
    { method: "GET" },
    leaveRegisterSchema,
  );
}

/// The caller's own balance per configured leave type. Self scoped by
/// construction: no membershipId parameter exists on this route. `asOf`
/// defaults to today in the workspace's own timezone when omitted.
export async function getMyBalances(workspaceId: string, asOf?: string): Promise<MyBalances> {
  return apiCall<MyBalances>(
    `${P.balancesMe(workspaceId)}${qs({ asOf })}`,
    { method: "GET" },
    myBalancesSchema,
  );
}

/// The leave years this person may look at, and which one to open on.
///
/// Takes no parameters at all: what a caller may see is resolved from their
/// session on the shore, so there is nothing here to widen.
export async function getMyLeavePeriods(workspaceId: string): Promise<MyLeavePeriods> {
  return apiCall<MyLeavePeriods>(P.periodsMe(workspaceId), { method: "GET" }, myLeavePeriodsSchema);
}

export interface MyApplicationsFilters {
  status?: string;
  from?: string;
  to?: string;
  limit?: number;
  cursor?: string;
}

/// The caller's own request history, newest leave first, cursor paged.
export async function listMyApplications(
  workspaceId: string,
  filters: MyApplicationsFilters = {},
): Promise<MyApplications> {
  return apiCall<MyApplications>(
    `${P.applicationsMe(workspaceId)}${qs({
      status: filters.status,
      from: filters.from,
      to: filters.to,
      limit: filters.limit?.toString(),
      cursor: filters.cursor,
    })}`,
    { method: "GET" },
    myApplicationsSchema,
  );
}

/// The headline band above the balance table. Same `asOf` the table takes, so
/// the two describe the same day.
export async function getBalanceSummary(
  workspaceId: string,
  asOf?: string,
): Promise<LeaveBalanceSummary> {
  return apiCall<LeaveBalanceSummary>(
    `${P.balances(workspaceId)}/summary${qs({ asOf })}`,
    { method: "GET" },
    leaveBalanceSummarySchema,
  );
}

export interface BalanceReportFilters {
  asOf?: string;
  departmentId?: string;
  membershipId?: string;
  page?: number;
  pageSize?: number;
}

/// The liability table. Breadth comes from the caller's permissions: a
/// member gets themselves, a manager their subtree, an admin the workspace.
/// `departmentId`/`membershipId` NARROW that; they can never widen it.
export async function getBalanceReport(
  workspaceId: string,
  filters: BalanceReportFilters = {},
): Promise<BalanceReport> {
  return apiCall<BalanceReport>(
    `${P.balances(workspaceId)}${qs({
      asOf: filters.asOf,
      departmentId: filters.departmentId,
      membershipId: filters.membershipId,
      page: filters.page?.toString(),
      pageSize: filters.pageSize?.toString(),
    })}`,
    { method: "GET" },
    balanceReportSchema,
  );
}

/// Leave on the calendar for whoever the caller may see. `from`/`to` are
/// required by the route; span is capped at 366 days and items at 1,000,
/// both a 400 with no partial page and no cursor to page around it: keep the
/// range modest, or narrow to one person.
export async function getLeaveCalendar(
  workspaceId: string,
  filters: { from: string; to: string; membershipId?: string },
): Promise<LeaveCalendar> {
  return apiCall<LeaveCalendar>(
    `${P.calendar(workspaceId)}${qs(filters)}`,
    { method: "GET" },
    leaveCalendarSchema,
  );
}

/// Everyone approved-out today, workspace wide. `date` defaults to today in
/// the workspace's own timezone.
export async function getOutToday(workspaceId: string, date?: string): Promise<OutToday> {
  return apiCall<OutToday>(
    `${P.outToday(workspaceId)}${qs({ date })}`,
    { method: "GET" },
    outTodaySchema,
  );
}

/// Every row behind one person's balance on one leave type. Admin only
/// (LEAVE_VIEW_WORKSPACE). `allocationId` defaults to whichever allocation
/// covers `asOf`.
export async function getLeaveLedger(
  workspaceId: string,
  filters: { membershipId: string; leaveTypeId: string; allocationId?: string; asOf?: string },
): Promise<LedgerView> {
  return apiCall<LedgerView>(
    `${P.ledger(workspaceId)}${qs(filters)}`,
    { method: "GET" },
    ledgerViewSchema,
  );
}
